import { Box, Button, ButtonGroup, Card, CardContent, CardHeader, Grid, Tab, Tabs, Typography, Container } from "@mui/material"
import { MainLoggedInLayout } from "../../components/layouts/main.loggedIn.layout"
import { useNavigate } from "react-router"
import { useContext, useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { UserContext } from "../../context/UserContext";
import { searchAllForums } from "../../controllers/forum/forum";
import { para_label } from "../community/list";
import { CategoryDisplay } from "../../components/category.display";
import { SearchWidget } from "../../components/search.widget";
import CategoryFilter from "../../components/category.filter";
import { Tune } from "@mui/icons-material";
import { useProfileComplete } from "../../hooks/useProfileComplete";

export const CommunityCard = ({card_details, navigate}) => {
    const truncatedTitle = card_details.title.split("Original post: ")[0].slice(0, 155);
    const shouldTruncate = (card_details.title.length - 150) > 156;
    
    return(
        <Card 
            sx={{ 
                position: "relative", 
                cursor: 'pointer', 
                height: 180,
                borderRadius: 2,
                overflow: 'hidden',
                transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
                backgroundColor: '#f5f5f5',
                border: '1px solid #e0e0e0',
                '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 8px 25px rgba(0,0,0,0.15)',
                    backgroundColor: '#ffffff'
                }
            }} 
            onClick={() => navigate("/forum/show/"+card_details?._id)}
        >      
            <Box
                sx={{
                    height: '100%',
                    position: 'relative',
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    display: 'flex',
                    flexDirection: 'column'
                }}
            >
                <Box
                    sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: 'linear-gradient(to bottom, rgba(0,0,0,0.4) 0%, rgba(0,0,0,0.7) 100%)'
                    }}
                />
                <Box
                    sx={{
                        position: 'relative',
                        zIndex: 2,
                        p: 2,
                        color: '#ffffff',
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'space-between'
                    }}
                >
                    <Typography 
                        variant="body1" 
                        sx={{ 
                            fontWeight: 'medium',
                            lineHeight: 1.4,
                            fontSize: '0.9rem',
                            overflow: 'hidden',
                            display: '-webkit-box',
                            WebkitLineClamp: 4,
                            WebkitBoxOrient: 'vertical'
                        }}
                    >
                        {truncatedTitle}{shouldTruncate && " ..."}
                    </Typography>
                    <Box sx={{ mt: 'auto' }}>
                        <CategoryDisplay label={card_details.category} hideLabel={true} />
                    </Box>
                </Box>
            </Box>
        </Card>
    )
}

function CustomTabPanel(props) {
    const { children, value, index, ...other } = props;
  
    return (
      <div
        role="tabpanel"
        hidden={value !== index}
        id={`simple-tabpanel-${index}`}
        aria-labelledby={`simple-tab-${index}`}
        {...other}
      >
        {value === index && <Box>{children}</Box>}
      </div>
    );
  }

  function a11yProps(index) {
    return {
      id: `simple-tab-${index}`,
      'aria-controls': `simple-tabpanel-${index}`,
    };
  }

export const ListForum = () => {
    const { userDetails } = useContext(UserContext)
    let token = userDetails?.token
    
    // Check profile completion and redirect if needed
    useProfileComplete(userDetails)
    
    const [title, setTitle] =  useState('')
    const [showCatFilter, setShowCatFilter] = useState(false)
    const [page_number, setPageNum] = useState(0)
    const [category, setCategory] = useState('')
    const [categories, setCategories] = useState([])
    let limit = 14
    const { data:forums, refetch } = useQuery({ queryKey: ['searchAllForums'], queryFn: () => searchAllForums({token, title, page_number, category, limit}) })
    useEffect(() => {
        refetch()
    })

    let listForumTopics = useMemo(() => forums?.forum ?? [],[forums?.forum])
    const [allForumTopics, setAllForumTopics] = useState([])
    const [tempForumTopics, setTempForumTopics] = useState([])

    useEffect(() => {
        listForumTopics.filter(item => setTempForumTopics(allForumTopics => [...allForumTopics, item]))
        let localCats = []
        listForumTopics.filter(item => item.category && !localCats.includes(item.category) && localCats.push(item.category))
        setCategories(localCats)
    }, [listForumTopics])

    useEffect(() => {
        let jsonObject = tempForumTopics.map(JSON.stringify);
        let uniqueSet = new Set(jsonObject);
        let uniqueArray = Array.from(uniqueSet).map(JSON.parse);

        setAllForumTopics(uniqueArray)
    }, [tempForumTopics])
    
    let navigate = useNavigate()
    const [value, setValue] = useState(1);

    const handleChange = (event, newValue) => {
        setValue(newValue);
    };

    useEffect(() => {
      setPageNum(0)
      if(title || category){
        setAllForumTopics([])
        setTempForumTopics([])
      }
    }, [title, category])
    
    
    const handleScroll = (e) => {
        const bottom = e.target.scrollHeight - e.target.scrollTop - e.target.clientHeight < 10;
        if (bottom && listForumTopics.length === limit) { 
            setTimeout(() => {
                setPageNum(page_number+1)
            }, 100);
        }
    }

    // Add scroll event listener to the container
    useEffect(() => {
        const container = document.querySelector('[data-scroll-container]');
        if (container) {
            container.addEventListener('scroll', handleScroll);
            return () => container.removeEventListener('scroll', handleScroll);
        }
    }, [handleScroll]);
    return (
        <MainLoggedInLayout>
            <Container maxWidth="lg" sx={{ py: 2 }}>
                <Card sx={{ 
                    overflowY: 'auto', 
                    maxHeight: '95vh', 
                    scrollbarWidth: 'none',
                    borderRadius: 2,
                    boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
                }}
                data-scroll-container
                onScroll={handleScroll}
                >
                    <CardHeader 
                        title={
                            <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold' }}>
                                {value === 0 ? "My Topics" : "All Topics"}
                            </Typography>
                        }
                        subheader={
                            <Button 
                                variant="contained" 
                                size="large"
                                onClick={() => navigate("/forum/create")}
                                sx={{ 
                                    mt: 1,
                                    borderRadius: 2,
                                    textTransform: 'none',
                                    fontWeight: 'bold'
                                }}
                            >
                                Create A Topic
                            </Button>
                        }
                        sx={{ pb: 1 }}
                    />

                    <Box sx={{ borderBottom: 1, borderColor: 'divider', mx: 2 }}>
                        <Tabs 
                            value={value} 
                            onChange={handleChange} 
                            aria-label="forum tabs"
                            sx={{
                                '& .MuiTab-root': {
                                    textTransform: 'none',
                                    fontWeight: 'bold',
                                    fontSize: '1rem'
                                }
                            }}
                        >
                            <Tab label="My Topics" {...a11yProps(0)} />
                            <Tab label="All Topics" {...a11yProps(1)} />
                        </Tabs>
                    </Box>

                    {allForumTopics.length > 0 && (
                        <Box sx={{ p: 2, backgroundColor: '#f8f9fa' }}>
                            <Box sx={{ mb: 2 }}>
                                <SearchWidget search={title} setSearch={setTitle} />
                            </Box>
                            <ButtonGroup variant="outlined" sx={{ borderRadius: 2 }}>
                                <Button 
                                    onClick={() => setShowCatFilter(!showCatFilter)}
                                    sx={{ 
                                        textTransform: 'none',
                                        fontWeight: 'bold'
                                    }}
                                >
                                    Category <Tune sx={{ ml: 1 }} />
                                </Button>
                            </ButtonGroup>
                            {showCatFilter && (
                                <Box sx={{ mt: 2 }}>
                                    <CategoryFilter catgories={categories} category={category} setCategory={setCategory} />
                                </Box>
                            )}
                        </Box>
                    )}

                    <CustomTabPanel value={value} index={0}>
                        <CardContent sx={{ p: 3 }}>
                            {allForumTopics.filter(topic => 
                                topic.author?.includes(userDetails?.id)
                            ).length > 0 ? (
                                <Grid container spacing={3}>
                                    {allForumTopics.map((topic, index) => (
                                        topic.author?.includes(userDetails?.id) && (
                                            <Grid item xs={12} sm={6} md={4} lg={3} key={index}>
                                                <CommunityCard card_details={topic} navigate={navigate} userDetails={userDetails} />
                                            </Grid>
                                        )
                                    ))}
                                </Grid>
                            ) : (
                                <Box sx={{ textAlign: 'center', py: 8 }}>
                                    <Typography variant="h6" color="text.secondary" gutterBottom>
                                        You haven't created any topics yet
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                                        Start discussions and engage with the community
                                    </Typography>
                                    <Button 
                                        variant="contained" 
                                        onClick={() => setValue(1)}
                                        sx={{ mr: 2 }}
                                    >
                                        Browse All Topics
                                    </Button>
                                    <Button 
                                        variant="outlined" 
                                        onClick={() => navigate("/forum/create")}
                                    >
                                        Create New Topic
                                    </Button>
                                </Box>
                            )}
                        </CardContent>
                    </CustomTabPanel>
                    
                    <CustomTabPanel value={value} index={1}>
                        <CardContent sx={{ p: 3 }}>
                            {allForumTopics.length > 0 ? (
                                <Grid container spacing={3}>
                                    {allForumTopics.map((topic, index) => (
                                        <Grid item xs={12} sm={6} md={4} lg={3} key={index}>
                                            <CommunityCard card_details={topic} navigate={navigate} userDetails={userDetails} />
                                        </Grid>
                                    ))}
                                </Grid>
                            ) : (
                                <Box sx={{ textAlign: 'center', py: 8 }}>
                                    <Typography variant="h6" color="text.secondary" gutterBottom>
                                        No topics found
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                                        Be the first to start a discussion and engage the community
                                    </Typography>
                                    <Button 
                                        variant="contained" 
                                        onClick={() => navigate("/forum/create")}
                                    >
                                        Create First Topic
                                    </Button>
                                </Box>
                            )}
                        </CardContent>
                    </CustomTabPanel>
                </Card>
            </Container>
        </MainLoggedInLayout>
    )
}